import React, { useEffect, useRef } from 'react';
import './Configurator.css';

// Phase 1: everything the settings panel needs (data loader, pricing, cart and
// the configurator UI itself). Small, so the options are on screen right away.
// custom-configurator.js doesn't export anything — the moment it runs, it
// immediately reads window.__ORION_CFG_ROOT__ and mounts itself into
// #custom-configurator right then. It never runs again on its own, so
// leaving the original browser tag in place after a React Router trip
// away from /configurator and back means this never re-fires for the new
// shadow root: the placeholder just spins forever. rerun forces a fresh
// <script> tag (and therefore a fresh mount) on every visit.
const UI_SCRIPTS = [
  { src: '/configurator/js/runtime-config.js' },
  // cabinet3d.js calls window.OrionSite?.glbUrl()/path() to resolve GLB and
  // logo-texture URLs. agorateh doesn't load metallic's full site.js (that
  // also builds the shared header/footer, which we don't want here), so this
  // tiny shim provides just those two functions — without it, every GLB
  // request falls back to a path missing the "/glb" segment and 404s.
  { src: '/configurator/js/orion-site-shim.js' },
  { src: '/configurator/js/config-loader.js' },
  { src: '/configurator/js/glb-prefetch.js' },
  { src: '/configurator/js/cart.js?v=2' },
  { src: '/configurator/js/configurator-pricing.js' },
  { src: '/configurator/js/custom-configurator.js', rerun: true },
];

// Phase 2: the heavy 3D part (three.js, GLTF loader, the viewer). Fetched in
// the background once phase 1 is in; custom-configurator.js waits for the
// "orion-3d-ready" event and then attaches the viewer to the already-visible UI.
const VIEWER_SCRIPTS = [
  { src: '/configurator/js/vendor/three.min.js' },
  { src: '/configurator/js/orion-gltf.js', module: true },
  { src: '/configurator/js/cabinet3d.js' },
];

function loadScript(src, { module = false, rerun = false } = {}) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (rerun) {
        existing.remove();
      } else if (existing.dataset.loaded === '1') {
        resolve();
        return;
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
        return;
      }
    }
    const script = document.createElement('script');
    script.src = src;
    if (module) {
      script.type = 'module';
    } else {
      // Scripts inserted via appendChild default to async=true (fetched AND
      // executed as soon as each one is ready, out of order) unless told
      // otherwise. Setting async=false restores the normal <script> contract:
      // the browser still fetches every tag in parallel, but runs them in
      // the order they were appended — so cabinet3d.js still sees THREE
      // (from three.min.js) already defined, etc.
      script.async = false;
    }
    script.onload = () => { script.dataset.loaded = '1'; resolve(); };
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

const Configurator = () => {
  const hostRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current || !hostRef.current) return;
    startedRef.current = true;

    const shadowRoot = hostRef.current.attachShadow({ mode: 'open' });
    shadowRoot.innerHTML = `
      <link rel="stylesheet" href="/configurator/css/styles.css">
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
      <link href="https://fonts.googleapis.com/css2?family=Golos+Text:wght@400;500;600;700&family=Unbounded:wght@500;600;700;800&display=swap" rel="stylesheet">
      <style>
        /* styles.css defines its whole palette on :root — which only ever
           matches the real document root, never a shadow root. Every var()
           using one of these tokens (nearly all of them — backgrounds,
           text, borders…) was silently resolving to nothing in here, e.g.
           the hero's "dark navy" background was actually invisible,
           showing the white host underneath instead. Re-declare the same
           tokens on :host so the shared stylesheet renders as intended.
           --header-h also matches this app's real fixed header (Header.css)
           instead of the 72px the orion-metallic page assumes for its own. */
        :host {
          --navy-950: #071525;
          --navy-900: #0b1f3a;
          --navy-800: #143a66;
          --navy-700: #1e4f85;
          --gold-500: #d4a017;
          --gold-400: #e8b923;
          --gold-300: #f0cc55;
          --steel-100: #f5f7fa;
          --steel-200: #e8eef5;
          --steel-300: #d0dae8;
          --steel-400: #9aadc4;
          --ink: #0f1724;
          --ink-muted: #4a5a70;
          --white: #ffffff;
          --danger: #c0392b;
          --ok: #1f7a4d;
          --radius: 4px;
          --radius-lg: 10px;
          --shadow: 0 12px 40px rgba(11, 31, 58, 0.08);
          --shadow-lg: 0 24px 60px rgba(11, 31, 58, 0.14);
          --font-display: "Unbounded", system-ui, sans-serif;
          --font-body: "Golos Text", system-ui, sans-serif;
          --max: 1320px;
          --ease: cubic-bezier(0.22, 1, 0.36, 1);
          --glow-gold: min(52rem, 68vw, 900px);
          --glow-gold-sm: min(38rem, 56vw, 680px);
          --header-h: 80px;
        }
        @media (max-width: 1024px) {
          :host { --header-h: 70px; }
        }
        /*
          CSS grid items default to min-width: auto, so the stage/panel columns
          refuse to shrink below their content's intrinsic width (nowrap hint
          text, long material labels, etc.), blowing the grid out past the
          viewport on narrow screens. min-width: 0 lets them shrink as intended.
        */
        .cfg,
        .cfg__stage,
        .cfg__viewport,
        .cfg__canvas,
        .cfg__panel,
        .cfg__menu-item { min-width: 0; }
        /* Tap target: the space/ship backdrop switch is only ~23px tall. */
        .cfg__bg-btn.cfg__bg-btn { min-height: 32px; }
      </style>
      <main class="page-cfg">
        <section class="page-hero page-hero--compact page-hero--cfg">
          <div class="page-hero__atmosphere" aria-hidden="true">
            <div class="page-hero__glow"></div>
            <div class="page-hero__grid"></div>
          </div>
          <div class="container page-hero__shell">
            <div class="page-hero__content">
              <h1>3D-конфигуратор корпусов</h1>
              <p class="page-hero__lead">Соберите напольный, навесной шкаф или коробку в 3D: размеры, комплектация и цвет RAL — расчёт цены онлайн.</p>
            </div>
          </div>
        </section>
        <section class="section section--cfg" aria-labelledby="cfg-title">
          <div class="container cfg-wrap">
            <h2 id="cfg-title" class="visually-hidden">Конфигуратор</h2>
            <div id="custom-configurator">
              <!-- Same grid as the real .cfg so nothing jumps when it is swapped in. -->
              <div class="cfg cfg--skeleton" aria-busy="true">
                <div class="cfg__stage">
                  <div class="cfg__viewport" data-bg="tech">
                    <div class="cfg__fx" aria-hidden="true">
                      <div class="cfg__fx-base"></div>
                      <div class="cfg__fx-aurora"></div>
                      <div class="cfg__fx-vignette"></div>
                    </div>
                    <div class="cfg__glb-loading is-indeterminate" role="status" aria-live="polite">
                      <div class="cfg__glb-loading-label">Загружаем конфигуратор…</div>
                      <div class="cfg__glb-loading-track" aria-hidden="true"><div class="cfg__glb-loading-bar"></div></div>
                    </div>
                  </div>
                </div>
                <div class="cfg__panel" aria-hidden="true">
                  <span class="cfg__skel cfg__skel--title"></span>
                  <span class="cfg__skel"></span>
                  <span class="cfg__skel"></span>
                  <span class="cfg__skel cfg__skel--short"></span>
                  <span class="cfg__skel"></span>
                  <span class="cfg__skel"></span>
                  <span class="cfg__skel cfg__skel--short"></span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    `;
    window.__ORION_CFG_ROOT__ = shadowRoot;

    // Phase 1 first so the settings are interactive as early as possible, then
    // the heavy 3D scripts. Within a phase every tag's fetch starts in the same
    // tick (concurrent over HTTP/2) while its code still runs in list order, so
    // load-bearing globals (THREE, OrionCabinet3D, …) are ready when the next
    // script needs them.
    const run = (list) => Promise.all(list.map(({ src, module, rerun }) => loadScript(src, { module, rerun })));
    run(UI_SCRIPTS)
      .catch((err) => console.error('Configurator UI failed to load', err))
      .then(() => run(VIEWER_SCRIPTS))
      .then(() => window.dispatchEvent(new Event('orion-3d-ready')))
      .catch((err) => {
        console.error('Configurator 3D failed to load', err);
        window.__ORION_3D_FAILED__ = true;
        window.dispatchEvent(new Event('orion-3d-error'));
      });
  }, []);

  return <div ref={hostRef} className="configurator-host" />;
};

export default Configurator;
